import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import 'rsuite/dist/rsuite-no-reset.min.css';
import '@app/styles/global.scss';
import Layout from '@shared/ui/Layout';
import DashboardPage from '@pages/private/DashboardPage';
import CardsPage from '@pages/private/CardsPage';
import TransactionsPage from '@pages/private/TransactionsPage';
import TeamsPage from '@pages/private/TeamsPage';
import ProfilePage from '@pages/private/ProfilePage';
import ReferralsPage from '@pages/private/ReferralsPage';
import BalancesPage from '@pages/private/BalancesPage';
import LoginPage from '@pages/public/LoginPage';
import SignUpPage from '@pages/public/SignUpPage';
import { store, persistor } from '@app/store';
import {
  DialogProvider,
  DialogViewport,
} from '@app/components/dialogs/DialogProvider';
import {
  DrawerProvider,
  DrawerViewport,
} from '@app/components/dialogs/DrawerProvider';
import TeamsIdPage from '@pages/private/TeamsIdPage';
import ForgotPasswordPage from '@pages/public/ForgotPasswordPage';
import ResetPasswordPage from '@pages/public/ResetPasswordPage';
import NotFoundPage from '@pages/private/NotFoundPage';
import PrivateRoute from '@app/components/PrivateRoute';
import { CustomProvider } from 'rsuite';
import ActivateUserPage from '@pages/public/ActivateUserPage';
import 'moment/locale/ru';
import ru from 'rsuite/locales/ru_RU';
import en from 'rsuite/locales/en_US';
import { useTranslation } from 'react-i18next';
import { PersistGate } from 'redux-persist/integration/react';
import { rsuiteComponents, theme } from '@utils/libs/theme';
import { initColorTheme } from '@utils/hooks/useThemeToggle';
import UserInitializer from '@app/components/UserInitializer';
import MainPreloader from '@app/components/MainPreloader';
import DocumentHead from '@app/components/DocumentHead';
import { OrderIdPage } from '@pages/public/OrderIdPage';
import SupportPage from '@pages/private/SupportPage';
import { ToasterBridge } from '@shared/ui/messages/useToaster';

initColorTheme();

function App() {
  const { i18n } = useTranslation();

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CustomProvider
            locale={i18n.resolvedLanguage === 'ru' ? ru : en}
            components={rsuiteComponents}
          >
            {/* нужен для showToaster в thunk'ах, см. useToaster.js */}
            <ToasterBridge />
            <DocumentHead />
            <MainPreloader />
            <Router>
              <DrawerProvider>
                <DialogProvider>
                  <UserInitializer>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/sign-up" element={<SignUpPage />} />
                      <Route
                        path="/activate-user"
                        element={<ActivateUserPage />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                      />
                      <Route path="/order/:id" element={<OrderIdPage />} />

                      <Route
                        path="/"
                        element={
                          <PrivateRoute>
                            <Layout />
                          </PrivateRoute>
                        }
                      >
                        <Route index element={<DashboardPage />} />
                        <Route path="cards" element={<CardsPage />} />
                        <Route
                          path="transactions"
                          element={<TransactionsPage />}
                        />
                        <Route path="teams" element={<TeamsPage />} />
                        <Route path="teams/:slug" element={<TeamsIdPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="referrals" element={<ReferralsPage />} />
                        <Route path="support" element={<SupportPage />} />
                        <Route path="balances" element={<BalancesPage />} />

                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Routes>
                  </UserInitializer>
                  <DialogViewport />
                  <DrawerViewport />
                </DialogProvider>
              </DrawerProvider>
            </Router>
          </CustomProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
